import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { LOGO_URL, buildWhatsAppUrl } from "@/lib/site";
import { treatmentsCatalog } from "@/data/catalog";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);

  const WHATSAPP_URL = buildWhatsAppUrl("default", lang);

  const onLogoClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      e.preventDefault();
      navigate("/");
      // ScrollToTop component will handle resetting scroll
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdown(false);
    setMobileSubOpen(false);
  }, [pathname]);

  const isHome = pathname === "/";
  const darkTop = isHome && !scrolled;

  const navItems = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/tratamientos", label: t.nav.treatments, key: "treatments", hasDropdown: true },
    // { to: "/promociones", label: t.nav.promotions, key: "promotions" }, // OCULTA temporalmente — accesible por URL directa
    { to: "/blog", label: t.nav.blog, key: "blog" },
    { to: "/trabaja-con-nosotros", label: t.nav.jobs, key: "jobs" },
    { to: "/contacto", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/95 backdrop-blur-md border-b border-ink/10"
          : darkTop
          ? "bg-transparent"
          : "bg-cream"
      }`}
      onMouseLeave={() => setDropdown(false)}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="header-logo-link"
          onClick={onLogoClick}
          className="flex items-center gap-3 shrink-0"
          aria-label="Iñaki Mayo Dental"
        >
          <img
            src={LOGO_URL}
            alt="Iñaki Mayo Dental"
            className={`h-10 md:h-11 w-auto transition-all duration-500 ${
              darkTop ? "" : "invert"
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {navItems.map((item) => {
            const baseClasses = ({ isActive }) =>
              `link-underline text-[13px] tracking-wide leading-none transition-colors ${
                darkTop ? "text-cream/90 hover:text-cream" : "text-ink/85 hover:text-ink"
              } ${isActive ? "font-medium" : "font-light"}`;

            if (item.hasDropdown) {
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  data-testid={`nav-link-${item.key}`}
                  onMouseEnter={() => setDropdown(true)}
                  className={(p) => `${baseClasses(p)} relative pr-4`}
                >
                  {item.label}
                  <ChevronDown
                    size={11}
                    strokeWidth={1.8}
                    aria-hidden
                    className={`absolute right-0 top-1/2 -translate-y-1/2 transition-transform duration-300 ${
                      dropdown ? "rotate-180" : ""
                    }`}
                  />
                </NavLink>
              );
            }
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`nav-link-${item.key}`}
                className={baseClasses}
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className={`hidden sm:flex items-center text-[11px] tracking-widest border ${
              darkTop ? "border-cream/30 text-cream" : "border-ink/25 text-ink"
            }`}
            data-testid="language-toggle"
          >
            <button
              onClick={() => setLang("es")}
              data-testid="lang-es"
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "es" ? (darkTop ? "bg-cream text-ink" : "bg-ink text-cream") : ""
              }`}
            >
              ES
            </button>
            <button
              onClick={() => setLang("ca")}
              data-testid="lang-ca"
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "ca" ? (darkTop ? "bg-cream text-ink" : "bg-ink text-cream") : ""
              }`}
            >
              CA
            </button>
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-btn"
            className={`hidden md:inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.22em] uppercase transition-all btn-sweep ${
              darkTop
                ? "border border-cream text-cream hover:text-ink sweep-cream"
                : "border border-ink text-ink hover:text-cream sweep-ink"
            }`}
          >
            {t.nav.cta}
            <span aria-hidden>→</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${darkTop ? "text-cream" : "text-ink"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Treatments Mega Dropdown */}
      <div
        data-testid="treatments-dropdown"
        className={`hidden lg:block absolute left-0 right-0 top-full transition-all duration-300 ${
          dropdown ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onMouseEnter={() => setDropdown(true)}
        onMouseLeave={() => setDropdown(false)}
      >
        <div className="bg-ink text-cream border-t border-cream/10 shadow-2xl">
          <div className="max-w-[1400px] mx-auto px-10 py-10 grid grid-cols-12 gap-8">
            <div className="col-span-3">
              <p className="overline text-cream/50">{t.nav.treatments}</p>
              <h3 className="font-serif text-3xl font-light leading-tight mt-3">
                {t.treatments.title}
              </h3>
              <Link
                to="/tratamientos"
                data-testid="dropdown-view-all"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-cream text-cream text-[10px] tracking-[0.22em] uppercase btn-sweep sweep-cream hover:text-ink"
              >
                {t.nav.view_all} →
              </Link>
            </div>
            <div className="col-span-9 grid grid-cols-4 gap-x-6 gap-y-3">
              {treatmentsCatalog.map((tr) => {
                const data = tr[lang];
                return (
                  <Link
                    key={tr.key}
                    to={`/tratamientos#${tr.key}`}
                    data-testid={`dropdown-treatment-${tr.key}`}
                    className="group block border-t border-cream/15 pt-3 hover:border-cream transition-colors"
                  >
                    <p className="overline text-cream/40 group-hover:text-cream/70">
                      {data.sub}
                    </p>
                    <p className="font-serif text-xl font-light mt-1 leading-snug group-hover:translate-x-1 transition-transform">
                      {data.name}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-ink text-cream max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-8 space-y-5">
            {navItems.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div key={item.key}>
                    <button
                      data-testid={`mobile-nav-${item.key}-toggle`}
                      onClick={() => setMobileSubOpen((v) => !v)}
                      className="w-full flex items-center justify-between font-serif text-3xl font-light"
                    >
                      {item.label}
                      <ChevronDown size={20} className={mobileSubOpen ? "rotate-180" : ""} />
                    </button>
                    {mobileSubOpen && (
                      <div className="mt-4 pl-4 border-l border-cream/20 space-y-3" data-testid="mobile-treatments-list">
                        <Link to="/tratamientos" data-testid="mobile-treatments-all" className="block font-light text-base">
                          {t.nav.view_all}
                        </Link>
                        {treatmentsCatalog.map((tr) => {
                          const data = tr[lang];
                          return (
                            <Link
                              key={tr.key}
                              to={`/tratamientos#${tr.key}`}
                              data-testid={`mobile-treatment-${tr.key}`}
                              className="block font-light text-base text-cream/85 hover:text-cream"
                            >
                              {data.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  data-testid={`mobile-nav-${item.key}`}
                  className="block font-serif text-3xl font-light"
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              );
            })}
            <div className="flex items-center gap-3 pt-4 border-t border-cream/20">
              <button onClick={() => setLang("es")} data-testid="mobile-lang-es" className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border border-cream/30 ${lang === "es" ? "bg-cream text-ink" : ""}`}>ES</button>
              <button onClick={() => setLang("ca")} data-testid="mobile-lang-ca" className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border border-cream/30 ${lang === "ca" ? "bg-cream text-ink" : ""}`}>CA</button>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-btn"
              className="inline-flex items-center gap-2 px-6 py-3 border border-cream text-xs tracking-[0.22em] uppercase"
            >
              {t.nav.cta} →
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
